import { useEffect, useState } from 'react';
import { Check, Info, X } from 'lucide-react';
import { contentSignedCounts, type ReadinessRow } from './documentReadinessHelpers';

interface SignOffModalProps {
  open: boolean;
  doc: ReadinessRow | null;
  userName: string;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}

/**
 * Sign-off confirmation modal. Captures an optional comment and shows a
 * warning when content is not fully signed off (since signing the document
 * before content closes pushes the row into the "Signed off — content open"
 * state).
 *
 * A2F-663 MVP: the comment field is free-form and stored verbatim. The
 * signed_file_hash column is left null. A2F-665 (Compliance follow-up)
 * replaces the comment with a §11.50-compliant attestation and pins the
 * file hash at sign time.
 */
export function DocumentReadinessSignOffModal({
  open,
  doc,
  userName,
  onClose,
  onConfirm,
}: SignOffModalProps) {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (open) setComment('');
  }, [open]);

  if (!doc) return null;

  const c = contentSignedCounts(doc);
  const contentOpen = c.total > 0 && c.signed < c.total;

  return (
    <div
      className={`fixed inset-0 z-40 flex items-center justify-center bg-black/30 transition-opacity ${
        open ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-label="Sign off"
        onClick={(e) => e.stopPropagation()}
        className="w-[480px] max-w-[92vw] overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="border-b border-slate-200 px-5 pt-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
              Document sign-off
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-7 w-7 place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <h2 className="mt-1 text-lg font-bold text-brand-charcoal">
            Sign off on {doc.name}?
          </h2>
          <p className="text-xs text-slate-500">
            You are signing off as <b className="font-semibold text-brand-charcoal">{userName}</b>.
          </p>
        </div>

        <div className="px-5 py-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono text-slate-500">{doc.section}</span>
            <span className="ml-auto text-slate-500">
              <b className="font-semibold text-brand-charcoal">
                {c.signed}/{c.total}
              </b>{' '}
              sections signed
            </span>
          </div>

          {contentOpen && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p>
                <b className="font-semibold">Content not fully signed off.</b>{' '}
                {c.total - c.signed} of {c.total} sections remain pending. The document will show{' '}
                <b className="font-semibold">"Signed off — content open"</b> until all sections are
                signed.
              </p>
            </div>
          )}

          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Comment <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span>
          </label>
          <textarea
            className="w-full resize-none rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange/30"
            rows={3}
            placeholder="Reviewed in full. No outstanding concerns."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <p className="text-[11px] text-slate-500">
            By signing off, you attest that you have reviewed this document.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(comment)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-orange px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600"
            >
              <Check className="h-3.5 w-3.5" /> Sign off
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
